import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import type { Order } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@nucleus-commerce/ui/components/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Label } from '@nucleus-commerce/ui/components/label';
import { RadioGroup, RadioGroupItem } from '@nucleus-commerce/ui/components/radio-group';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useRecordApproval } from '../hooks/use-order-mutations';
import {
  recordApprovalSchema,
  toRecordApprovalBody,
  type RecordApprovalForm,
} from '../schemas/order-edit-schemas';
import { orderConflictKey } from '../schemas/order-rules';

const KNOWN_FIELDS: ReadonlySet<string> = new Set(['outcome', 'note']);

export interface RecordApprovalDialogProps {
  storeId: string;
  order: Order;
  onOpenChange: (open: boolean) => void;
}

/**
 * The shopper approved the design, or asked for changes, by phone or email. Approval lifts the
 * hold (and captures a card authorisation) exactly like their own answer; when the workflow's
 * review stage waits, the answer is recorded there.
 */
export function RecordApprovalDialog({ storeId, order, onOpenChange }: RecordApprovalDialogProps) {
  const { t } = useTranslation(['orders', 'common']);
  const message = useErrorMessage();
  const record = useRecordApproval(storeId);
  const form = useForm<RecordApprovalForm>({
    resolver: useI18nResolver(recordApprovalSchema),
    defaultValues: { outcome: 'approved', note: '' },
  });
  const outcome = form.watch('outcome');

  const submit = form.handleSubmit(async (values) => {
    try {
      await record.mutateAsync({ id: order.id, body: toRecordApprovalBody(values) });
      onOpenChange(false);
    } catch (error) {
      if (applyFieldErrors(error, form.setError, { knownFields: KNOWN_FIELDS })) return;
      const conflict = orderConflictKey(error, 'orders:recordApproval.conflict');
      form.setError('root.server', {
        type: 'server',
        message: conflict ? t(conflict) : message(error),
      });
    }
  });

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('orders:recordApproval.title', { name: order.name })}</DialogTitle>
          <DialogDescription>{t('orders:recordApproval.description')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={submit} noValidate className="flex flex-col gap-4">
            {form.formState.errors.root?.server?.message ? (
              <Alert variant="destructive" role="alert">
                <AlertDescription>{form.formState.errors.root.server.message}</AlertDescription>
              </Alert>
            ) : null}
            <FormField
              control={form.control}
              name="outcome"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('orders:recordApproval.outcome')}</FormLabel>
                  <FormControl>
                    <RadioGroup value={field.value} onValueChange={field.onChange}>
                      {(['approved', 'changes_requested'] as const).map((option) => (
                        <div key={option} className="flex items-center gap-2">
                          <RadioGroupItem id={`approval-${option}`} value={option} />
                          <Label htmlFor={`approval-${option}`} className="font-normal">
                            {t(`orders:recordApproval.outcomes.${option}`)}
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="note"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {outcome === 'changes_requested'
                      ? t('orders:recordApproval.changes')
                      : t('orders:recordApproval.note')}
                  </FormLabel>
                  <FormControl>
                    <Textarea rows={3} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                {t('common:actions.cancel')}
              </Button>
              <Button type="submit" disabled={record.isPending}>
                {record.isPending
                  ? t('orders:recordApproval.submitting')
                  : t('orders:recordApproval.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
